import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LayoutGridIcon, ListIcon, SearchIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { CATEGORIES, COLORS, PRODUCTS } from '../data/products';
import { CategoryId } from '../types';
import { ProductCard } from '../components/ProductCard';
type SortKey = 'vendidos' | 'menor' | 'mayor' | 'novedades';
const SORTS: {
  id: SortKey;
  label: string;
}[] = [{
  id: 'vendidos',
  label: 'Más vendidos'
}, {
  id: 'menor',
  label: 'Precio menor'
}, {
  id: 'mayor',
  label: 'Precio mayor'
}, {
  id: 'novedades',
  label: 'Novedades'
}];
const MAX_PRICE = 5000;
const PAGE_SIZE = 9;
export function Catalog() {
  const [searchParams] = useSearchParams();
  const initialCat = searchParams.get('cat') as CategoryId | null;
  const [cats, setCats] = useState<CategoryId[]>(initialCat ? [initialCat] : []);
  const [maxPrice, setMaxPrice] = useState(MAX_PRICE);
  const [colorIds, setColorIds] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>('vendidos');
  const [query, setQuery] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const toggle = <T,>(arr: T[], value: T): T[] => arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
  const filtered = useMemo(() => {
    const result = PRODUCTS.filter(p => {
      const price = p.salePrice ?? p.price;
      if (cats.length && !cats.includes(p.category)) return false;
      if (price > maxPrice) return false;
      if (colorIds.length && !p.colors.some(c => colorIds.includes(c.id))) return false;
      if (query && !`${p.name} ${p.shortDescription} ${p.categoryLabel}`.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
    return result.sort((a, b) => {
      const pa = a.salePrice ?? a.price;
      const pb = b.salePrice ?? b.price;
      if (sort === 'menor') return pa - pb;
      if (sort === 'mayor') return pb - pa;
      if (sort === 'novedades') return Number(!!b.isNew) - Number(!!a.isNew);
      return b.reviews - a.reviews;
    });
  }, [cats, maxPrice, colorIds, query, sort]);
  const shown = filtered.slice(0, visible);
  const activeFilters = cats.length + colorIds.length + (maxPrice < MAX_PRICE ? 1 : 0);
  const clearAll = () => {
    setCats([]);
    setColorIds([]);
    setMaxPrice(MAX_PRICE);
    setQuery('');
  };
  const filterPanel = <div className="space-y-9" data-id="element-829" data-mp="x9uhu0cqqyuz">
      <fieldset data-id="element-830" data-mp="xanjwo0r906s">
        <legend className="font-display text-lg uppercase tracking-[0.18em] text-white" data-id="element-831" data-mp="x92pcyefxh8x">
          Categoría
        </legend>
        <div className="mt-4 space-y-3" data-id="element-832" data-mp="12b1p7yaz1w14">
          {CATEGORIES.map(cat => <label key={cat.id} className="flex cursor-pointer items-center gap-3 text-sm text-white/60 transition-colors hover:text-white" data-id="element-833" data-mp="3hyib3eysyfrp">
              <input type="checkbox" checked={cats.includes(cat.id)} onChange={() => {
            setCats(prev => toggle(prev, cat.id));
            setVisible(PAGE_SIZE);
          }} className="h-4 w-4 shrink-0 appearance-none border border-white/25 bg-transparent checked:border-pink-500 checked:bg-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500" data-id="element-834" data-mp="3egujnlhotljp" />
              <span className="flex-1" data-id="element-835" data-mp="2qsuv8s7um78a">{cat.short}</span>
              <span className="text-xs text-white/25" data-id="element-836" data-mp="2quebiiti8ia2">{cat.count}</span>
            </label>)}
        </div>
      </fieldset>

      <fieldset className="border-t border-white/10 pt-8" data-id="element-837" data-mp="30qr4feh86s3r">
        <legend className="font-display text-lg uppercase tracking-[0.18em] text-white" data-id="element-838" data-mp="30p657bjyukzk">
          Precio
        </legend>
        <input type="range" min={500} max={MAX_PRICE} step={100} value={maxPrice} onChange={e => {
        setMaxPrice(Number(e.target.value));
        setVisible(PAGE_SIZE);
      }} aria-label="Precio máximo" className="zadar-range mt-6" data-id="element-839" data-mp="2cytqnhu7tq00" />
        <div className="mt-3 flex items-center justify-between text-xs text-white/45" data-id="element-840" data-mp="1kb4qirhy970m">
          <span data-id="element-841" data-mp="1p2mvdqn3frqv">$ 500</span>
          <span className="font-semibold text-white" data-id="element-842" data-mp="1p2wmjhjvk4y5">
            Hasta $ {maxPrice.toLocaleString('es-UY')}
          </span>
        </div>
      </fieldset>

      <fieldset className="border-t border-white/10 pt-8" data-id="element-843" data-mp="1z00xsmc6mg06">
        <legend className="font-display text-lg uppercase tracking-[0.18em] text-white" data-id="element-844" data-mp="1yyg38wpvax2b">
          Color
        </legend>
        <div className="mt-4 flex gap-3" data-id="element-845" data-mp="23xjb0ox8fhdz">
          {COLORS.map(color => {
          const active = colorIds.includes(color.id);
          return <button key={color.id} type="button" onClick={() => {
            setColorIds(prev => toggle(prev, color.id));
            setVisible(PAGE_SIZE);
          }} aria-label={color.name} aria-pressed={active} className={`flex h-9 w-9 items-center justify-center rounded-full border transition-all ${active ? 'border-white scale-105' : 'border-white/20 hover:border-white/50'}`} data-id="element-846" data-mp="9mo0hh2hls8k">
                <span className="h-5 w-5 rounded-full" style={{
              backgroundColor: color.hex
            }} data-id="element-847" data-mp="p185c1wepyjq" />
              </button>;
        })}
        </div>
      </fieldset>

      <fieldset className="border-t border-white/10 pt-8" data-id="element-848" data-mp="fzryu58pqgks">
        <legend className="font-display text-lg uppercase tracking-[0.18em] text-white" data-id="element-849" data-mp="b3b7z3a4po40">
          Ordenar por
        </legend>
        <div className="mt-4 space-y-3" data-id="element-850" data-mp="30tew11o66pwb">
          {SORTS.map(option => <label key={option.id} className="flex cursor-pointer items-center gap-3 text-sm text-white/60 transition-colors hover:text-white" data-id="element-851" data-mp="2vrpinfdh6052">
              <input type="radio" name="sort" checked={sort === option.id} onChange={() => setSort(option.id)} className="h-4 w-4 shrink-0 appearance-none rounded-full border border-white/25 checked:border-[5px] checked:border-pink-500 focus:outline-none" data-id="element-852" data-mp="329982j2aziox" />
              {option.label}
            </label>)}
        </div>
      </fieldset>

      {activeFilters > 0 && <button type="button" onClick={clearAll} className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.16em] text-white/70 transition-colors hover:border-pink-500 hover:text-white" data-id="element-853" data-mp="24l8xlw37na5m">
          <XIcon className="h-3.5 w-3.5" data-id="element-854" data-mp="1vmwy14kd9211" /> Limpiar filtros
        </button>}
    </div>;
  return <div className="w-full bg-black" data-id="element-855" data-mp="1gxu6l2qqdsrb">
      <div className="border-b border-white/10 bg-ink-900" data-id="element-856" data-mp="1gywt6yh0t91f">
        <div className="mx-auto w-full max-w-7xl px-5 py-12 lg:px-8 lg:py-16" data-id="element-857" data-mp="1byr0rg2xfiia">
          <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-pink-500" data-id="element-858" data-mp="1c0vf6rygbzwt">
            Catálogo
          </span>
          <h1 className="mt-3 font-display text-5xl uppercase leading-none text-white sm:text-6xl" data-id="element-859" data-mp="1lx6tfnhlqztw">
            Todos los productos
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/50" data-id="element-860" data-mp="1zv53whssyc44">
            Bandas, guantes, tobilleras y correctores diseñados para entrenar
            fuerte sin castigar el cuerpo.
          </p>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-7xl gap-10 px-5 py-10 lg:px-8 lg:py-14" data-id="element-861" data-mp="24ng0pbs8xhsg">
        {/* SIDEBAR */}
        <aside className="hidden w-64 shrink-0 lg:block" data-id="element-862" data-mp="2imo4yj39ng3i">
          <div className="sticky top-28" data-id="element-863" data-mp="2il4nwq9sd10o">{filterPanel}</div>
        </aside>

        {/* MAIN */}
        <div className="min-w-0 flex-1" data-id="element-864" data-mp="2dpxmxq7wiafg">
          {/* TOP BAR */}
          <div className="flex flex-col gap-4" data-id="element-865" data-mp="3kzikbtoiq70o">
            <div className="relative" data-id="element-866" data-mp="3l2wnv58x4m9p">
              <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" data-id="element-867" data-mp="3pyvcmty5s6an" />
              <input type="search" value={query} onChange={e => {
              setQuery(e.target.value);
              setVisible(PAGE_SIZE);
            }} placeholder="Buscar productos..." aria-label="Buscar productos" className="w-full border border-white/10 bg-ink-800 py-3.5 pl-11 pr-4 text-sm text-white placeholder:text-white/35 focus:border-pink-500 focus:outline-none" data-id="element-868" data-mp="3u1rs4bsejmad" />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-869" data-mp="3g3k08ia83b62">
              <p className="text-xs uppercase tracking-[0.14em] text-white/45" data-id="element-870" data-mp="2iljv0pvqp363">
                Mostrando {shown.length} de {filtered.length} productos
              </p>
              <div className="flex items-center gap-2" data-id="element-871" data-mp="2g1wxb1neycnq">
                <button type="button" onClick={() => setFiltersOpen(true)} className="flex items-center gap-2 border border-white/15 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-white/70 transition-colors hover:border-pink-500 hover:text-white lg:hidden" data-id="element-872" data-mp="2fzasvavtkd9q">
                  <SlidersHorizontalIcon className="h-4 w-4" data-id="element-873" data-mp="1x3ufp7x73hzy" />
                  Filtros{activeFilters ? ` (${activeFilters})` : ''}
                </button>
                <div className="flex border border-white/10" data-id="element-874" data-mp="1nczij8ieuxzq">
                  <button type="button" onClick={() => setView('grid')} aria-label="Vista de cuadrícula" aria-pressed={view === 'grid'} className={`p-2.5 transition-colors ${view === 'grid' ? 'bg-pink-500 text-white' : 'text-white/45 hover:text-white'}`} data-id="element-875" data-mp="1ndiyijoi3cb4">
                    <LayoutGridIcon className="h-4 w-4" data-id="element-876" data-mp="p6v127fjtei9" />
                  </button>
                  <button type="button" onClick={() => setView('list')} aria-label="Vista de lista" aria-pressed={view === 'list'} className={`p-2.5 transition-colors ${view === 'list' ? 'bg-pink-500 text-white' : 'text-white/45 hover:text-white'}`} data-id="element-877" data-mp="13vit43q6r63g">
                    <ListIcon className="h-4 w-4" data-id="element-878" data-mp="l2exwdxbkl6q" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* GRID */}
          {shown.length === 0 ? <div className="mt-16 border border-dashed border-white/15 px-6 py-20 text-center" data-id="element-879" data-mp="1wlhoeguz3zgk">
              <h2 className="font-display text-3xl uppercase text-white" data-id="element-880" data-mp="1rmwko7giixnd">
                Sin resultados
              </h2>
              <p className="mx-auto mt-3 max-w-sm text-sm text-white/45" data-id="element-881" data-mp="242w86ih9jhv7">
                No encontramos productos con esos filtros. Probá ampliar el rango
                de precio o quitar algún color.
              </p>
              <button type="button" onClick={clearAll} className="mt-6 bg-pink-500 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-pink-600" data-id="element-882" data-mp="1u61eccvxq5n9">
                Limpiar filtros
              </button>
            </div> : <div className={`mt-8 ${view === 'grid' ? 'grid gap-5 sm:grid-cols-2 lg:grid-cols-3' : 'flex flex-col gap-5'}`} data-id="element-883" data-mp="290684s2a3bxa">
              {shown.map(product => <ProductCard key={product.id} product={product} view={view} data-id="element-884" data-mp="35kygmoh4qrck" />)}
            </div>}

          {visible < filtered.length && <div className="mt-12 flex justify-center" data-id="element-885" data-mp="3betmtoz3o4m3">
              <button type="button" onClick={() => setVisible(v => v + PAGE_SIZE)} className="border border-white px-8 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-white hover:text-black" data-id="element-886" data-mp="3bfbmkvkz10yr">
                Cargar más productos
              </button>
            </div>}
        </div>
      </div>

      {/* MOBILE FILTERS */}
      {filtersOpen && <div className="fixed inset-0 z-[60] lg:hidden" data-id="element-887" data-mp="2nq0vk5tfakxt">
          <div className="absolute inset-0 bg-black/70" onClick={() => setFiltersOpen(false)} data-id="element-888" data-mp="2ipv1yk3leod3" />
          <div className="absolute inset-y-0 left-0 w-[86%] max-w-sm overflow-y-auto border-r border-white/10 bg-ink-900 p-6" data-id="element-889" data-mp="2dr1lqjwx8j98">
            <div className="mb-8 flex items-center justify-between" data-id="element-890" data-mp="28s8h7gzm42l7">
              <h2 className="font-display text-2xl uppercase tracking-wide text-white" data-id="element-891" data-mp="1yojn76pxh4uh">
                Filtros
              </h2>
              <button type="button" onClick={() => setFiltersOpen(false)} aria-label="Cerrar filtros" className="p-2 text-white/60 hover:text-white" data-id="element-892" data-mp="23la601t4aejl">
                <XIcon className="h-5 w-5" data-id="element-893" data-mp="2dioglksd093z" />
              </button>
            </div>
            {filterPanel}
            <button type="button" onClick={() => setFiltersOpen(false)} className="mt-10 w-full bg-pink-500 py-4 text-[11px] font-bold uppercase tracking-[0.18em] text-white" data-id="element-894" data-mp="1kw3squb3ef56">
              Ver {filtered.length} productos
            </button>
          </div>
        </div>}
    </div>;
}